import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  createSavedImportMapping,
  dryrunGovernmentUserUpsert,
  getGovernmentUserFieldMappingForTempFile,
  listSavedImportMappings,
  updateSavedImportMapping,
  uploadTempFile,
  upsertGovernmentUsers,
} from "@/hera-api/client/sdk.gen";
import { invalidateAdminUserQueries } from "@/lib/admin-user-query-invalidation";
import {
  getErrorMessage,
  spreadsheetLineNumberFromImportError,
} from "@/lib/export-errors";
import { applySavedFieldMappingToSheetInfo } from "@/lib/import-saved-mapping";
import { getApiErrorMessage } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Upload } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ImportErrorsExportMenu } from "./import-errors-export-menu";

const GOVERNMENT_USER_FIELDS = [
  { key: "email", label: "Email" },
  { key: "name", label: "Name" },
  { key: "jurisdiction", label: "Jurisdiction" },
  { key: "is_main_contact", label: "Main Contact" },
  { key: "is_remittance_contact", label: "Remittance Contact" },
  { key: "active", label: "Active" },
] as const;

type ImportResult = {
  success_count: number;
  error_count: number;
  created_count: number;
  modified_count: number;
  unchanged_count: number;
  failed_count: number;
  issue_count: number;
  errors: Array<Record<string, unknown>>;
  issues: Array<Record<string, unknown>>;
  sample_data: Array<Record<string, unknown>>;
  all_data: Array<Record<string, unknown>>;
};

type SheetInfo = Record<
  string,
  {
    headers: string[];
    row_count: number;
    field_mapping?: Record<string, string>;
    mapping_warnings?: Array<{
      warning_type: string;
      field_key: string;
      mapped_value: string;
      message: string;
    }>;
  }
>;

interface GovernmentUserUploadSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

function ResultCounts({ result }: { result: ImportResult }) {
  const counts = [
    ["Created", result.created_count],
    ["Modified", result.modified_count],
    ["Unchanged", result.unchanged_count],
    ["Failed", result.failed_count],
  ] as const;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {counts.map(([label, value]) => (
        <div key={label} className="rounded border p-2 text-center">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="text-lg font-semibold">{value}</div>
        </div>
      ))}
    </div>
  );
}

function ResultDetails({
  result,
  filenamePrefix,
}: {
  result: ImportResult;
  filenamePrefix: string;
}) {
  return (
    <div className="space-y-4 rounded border p-3">
      <ResultCounts result={result} />
      {result.sample_data.length > 0 ? (
        <div>
          <div className="mb-1 text-sm font-medium">Preview</div>
          <div className="max-h-48 overflow-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr>
                  <th className="border px-2 py-1 text-left">Row</th>
                  <th className="border px-2 py-1 text-left">Email</th>
                  <th className="border px-2 py-1 text-left">Name</th>
                  <th className="border px-2 py-1 text-left">Jurisdiction</th>
                  <th className="border px-2 py-1 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {result.sample_data.map((row, index) => (
                  <tr key={`${String(row.email)}-${index}`}>
                    <td className="border px-2 py-1">
                      {spreadsheetLineNumberFromImportError(row, index)}
                    </td>
                    <td className="border px-2 py-1">
                      {String(row.email ?? "")}
                    </td>
                    <td className="border px-2 py-1">
                      {String(row.name ?? "")}
                    </td>
                    <td className="border px-2 py-1">
                      {String(row.jurisdiction_name ?? row.jurisdiction ?? "")}
                    </td>
                    <td className="border px-2 py-1 capitalize">
                      {String(row.action ?? "")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      {result.issues.length > 0 ? (
        <div>
          <div className="mb-1 text-sm font-medium text-amber-700">
            Issues ({result.issue_count})
          </div>
          <div className="max-h-28 space-y-1 overflow-y-auto">
            {result.issues.map((issue, index) => (
              <div
                className="rounded bg-amber-50 p-2 text-xs text-amber-900"
                key={`${String(issue.email)}-${String(
                  issue._original_row_index,
                )}-${getErrorMessage(issue)}`}
              >
                Row {spreadsheetLineNumberFromImportError(issue, index)}:{" "}
                {getErrorMessage(issue)}
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {result.errors.length > 0 ? (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-medium text-destructive">
              Errors ({result.error_count})
            </div>
            <ImportErrorsExportMenu
              errors={result.errors}
              filenamePrefix={filenamePrefix}
              uploadVariant
              visibleStrategy={{ kind: "simple" }}
            />
          </div>
          <div className="max-h-36 space-y-1 overflow-y-auto">
            {result.errors.map((error, index) => (
              <div
                className="rounded bg-destructive/10 p-2 text-xs text-destructive"
                key={`${String(error.email)}-${String(
                  error._original_row_index,
                )}-${getErrorMessage(error)}`}
              >
                Row {spreadsheetLineNumberFromImportError(error, index)}:{" "}
                {getErrorMessage(error)}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function GovernmentUserUploadSheet({
  isOpen,
  onOpenChange,
}: GovernmentUserUploadSheetProps) {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadToken, setUploadToken] = useState<string | null>(null);
  const [sheetInfo, setSheetInfo] = useState<SheetInfo | null>(null);
  const [selectedSheet, setSelectedSheet] = useState<string | null>(null);
  const [fieldMappings, setFieldMappings] = useState<
    Record<string, Record<string, string>>
  >({});
  const [isDragActive, setIsDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isMapping, setIsMapping] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dryRunResult, setDryRunResult] = useState<ImportResult | null>(null);
  const [applyResult, setApplyResult] = useState<ImportResult | null>(null);
  const [savedMappings, setSavedMappings] = useState<
    Array<{ id: string; name: string; field_mappings: Record<string, string> }>
  >([]);
  const [savedMappingId, setSavedMappingId] = useState("");
  const structuralSheetInfoRef = useRef<SheetInfo | null>(null);

  const reset = useCallback(() => {
    setSelectedFile(null);
    setUploadToken(null);
    setSheetInfo(null);
    setSelectedSheet(null);
    setFieldMappings({});
    setIsDragActive(false);
    setError(null);
    setDryRunResult(null);
    setApplyResult(null);
    setSavedMappingId("");
    structuralSheetInfoRef.current = null;
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) reset();
      onOpenChange(open);
    },
    [onOpenChange, reset],
  );

  const applyMappingState = useCallback((data: SheetInfo) => {
    setSheetInfo(data);
    const mappings: Record<string, Record<string, string>> = {};
    for (const [sheetName, info] of Object.entries(data)) {
      mappings[sheetName] = { ...(info.field_mapping ?? {}) };
    }
    setFieldMappings(mappings);
    setSelectedSheet((current) => current ?? Object.keys(data)[0] ?? null);
    setDryRunResult(null);
    setApplyResult(null);
  }, []);

  const loadSavedMappings = useCallback(async () => {
    try {
      const response = await listSavedImportMappings({
        query: { upload_type: "government_users" },
      });
      const data = ((response as { data?: unknown }).data ?? response) as {
        mappings?: Array<{
          id: string;
          name: string;
          field_mappings?: Record<string, string>;
        }>;
      };
      setSavedMappings(
        (data.mappings ?? []).map((mapping) => ({
          id: String(mapping.id),
          name: mapping.name,
          field_mappings: mapping.field_mappings ?? {},
        })),
      );
    } catch {
      setSavedMappings([]);
    }
  }, []);

  useEffect(() => {
    if (isOpen) void loadSavedMappings();
  }, [isOpen, loadSavedMappings]);

  const fetchMappings = useCallback(
    async (token: string, mappingId?: string) => {
      const response = await getGovernmentUserFieldMappingForTempFile({
        path: { token },
        query: mappingId ? { saved_mapping_id: mappingId } : {},
      });
      return ((response as { data?: unknown }).data ?? response) as SheetInfo;
    },
    [],
  );

  const selectSavedMapping = async (mappingId: string) => {
    setSavedMappingId(mappingId);
    if (!uploadToken) return;
    setIsMapping(true);
    setError(null);
    try {
      if (!mappingId && structuralSheetInfoRef.current) {
        applyMappingState(structuredClone(structuralSheetInfoRef.current));
      } else if (mappingId && structuralSheetInfoRef.current) {
        const saved = savedMappings.find((mapping) => mapping.id === mappingId);
        if (saved) {
          applyMappingState(
            applySavedFieldMappingToSheetInfo(
              structuralSheetInfoRef.current,
              saved.field_mappings,
            ) as SheetInfo,
          );
        }
      } else {
        applyMappingState(
          await fetchMappings(uploadToken, mappingId || undefined),
        );
      }
    } catch (caught) {
      setError(getApiErrorMessage(caught, "Could not apply saved mapping."));
    } finally {
      setIsMapping(false);
    }
  };

  const chooseFile = (file: File | undefined) => {
    if (!file) return;
    if (!/\.(csv|xlsx)$/i.test(file.name)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a .csv or .xlsx file.",
        variant: "destructive",
      });
      return;
    }
    reset();
    setSelectedFile(file);
  };

  const upload = async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    setIsMapping(true);
    setError(null);
    try {
      const response = await uploadTempFile({ body: { file: selectedFile } });
      const token = (
        ((response as { data?: unknown }).data ?? response) as { token: string }
      ).token;
      setUploadToken(token);
      const mappingData = await fetchMappings(
        token,
        savedMappingId || undefined,
      );
      structuralSheetInfoRef.current = structuredClone(mappingData);
      applyMappingState(mappingData);
      toast({
        title: "Upload successful",
        description: "Columns are ready to review.",
        variant: "secondary",
      });
    } catch (caught) {
      setError(getApiErrorMessage(caught, "Could not process the file."));
    } finally {
      setIsUploading(false);
      setIsMapping(false);
    }
  };

  const dryRun = async (sheetName: string) => {
    if (!uploadToken) return;
    setIsTesting(true);
    setError(null);
    setApplyResult(null);
    try {
      const response = await dryrunGovernmentUserUpsert({
        body: {
          token: uploadToken,
          sheet_name: sheetName,
          field_mappings: fieldMappings[sheetName] ?? {},
        },
      });
      const result = ((response as { data?: unknown }).data ??
        response) as ImportResult;
      setDryRunResult(result);
      toast({
        title: "Test processing complete",
        description: `${result.created_count} create, ${result.modified_count} modify, ${result.failed_count} failed`,
        variant: "secondary",
      });
    } catch (caught) {
      setError(getApiErrorMessage(caught, "Could not test this import."));
    } finally {
      setIsTesting(false);
    }
  };

  const apply = async () => {
    if (!uploadToken || !selectedSheet) return;
    setIsApplying(true);
    setError(null);
    try {
      const response = await upsertGovernmentUsers({
        body: {
          token: uploadToken,
          sheet_name: selectedSheet,
          field_mappings: fieldMappings[selectedSheet] ?? {},
        },
      });
      const result = ((response as { data?: unknown }).data ??
        response) as ImportResult;
      setApplyResult(result);
      setDryRunResult(null);
      await invalidateAdminUserQueries(queryClient);
      toast({
        title: "Government user upsert complete",
        description: `${result.created_count} created, ${result.modified_count} modified, ${result.failed_count} failed`,
        variant: result.failed_count > 0 ? "default" : "secondary",
      });
    } catch (caught) {
      setError(
        getApiErrorMessage(caught, "Could not upsert government users."),
      );
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-3xl">
        <SheetHeader>
          <SheetTitle>Upload Government Users</SheetTitle>
          <SheetDescription>
            Match users by email, create missing accounts, and patch existing
            accounts from mapped nonblank cells.
          </SheetDescription>
        </SheetHeader>
        <div className="flex max-h-[calc(100vh-120px)] flex-col gap-5 overflow-y-auto px-4 py-6">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <div className="mb-1 text-xs font-medium">Saved mapping</div>
              <select
                className="w-full rounded border px-2 py-1 text-xs"
                value={savedMappingId}
                onChange={(event) =>
                  void selectSavedMapping(event.target.value)
                }
              >
                <option value="">(No saved mapping selected)</option>
                {savedMappings.map((mapping) => (
                  <option key={mapping.id} value={mapping.id}>
                    {mapping.name}
                  </option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!selectedSheet || !uploadToken}
              onClick={async () => {
                if (!selectedSheet) return;
                const current = savedMappings.find(
                  (mapping) => mapping.id === savedMappingId,
                );
                const name = window.prompt(
                  "Name this mapping:",
                  current?.name ?? "",
                );
                if (!name?.trim()) return;
                try {
                  if (current?.name === name.trim()) {
                    await updateSavedImportMapping({
                      path: { mapping_id: current.id },
                      body: {
                        name: name.trim(),
                        field_mappings: fieldMappings[selectedSheet] ?? {},
                      },
                    });
                  } else {
                    await createSavedImportMapping({
                      body: {
                        name: name.trim(),
                        upload_type: "government_users",
                        field_mappings: fieldMappings[selectedSheet] ?? {},
                      },
                    });
                  }
                  await loadSavedMappings();
                  toast({
                    title: "Saved mapping updated",
                    description: name.trim(),
                    variant: "secondary",
                  });
                } catch (caught) {
                  setError(
                    getApiErrorMessage(caught, "Could not save the mapping."),
                  );
                }
              }}
            >
              Save mapping
            </Button>
          </div>

          {!selectedFile ? (
            <div
              className={`rounded border-2 border-dashed p-8 text-center ${
                isDragActive ? "border-primary bg-primary/10" : "border-muted"
              }`}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setIsDragActive(false);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setIsDragActive(false);
                chooseFile(event.dataTransfer.files[0]);
              }}
            >
              <Upload className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
              <div className="font-medium">Drag a CSV or XLSX file here</div>
              <label className="mt-2 inline-block cursor-pointer text-sm text-primary underline">
                Choose file
                <input
                  className="hidden"
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={(event) => chooseFile(event.target.files?.[0])}
                />
              </label>
            </div>
          ) : (
            <div className="flex items-center justify-between rounded border p-3">
              <span className="text-sm font-medium">{selectedFile.name}</span>
              <Button variant="outline" size="sm" onClick={reset}>
                Remove
              </Button>
            </div>
          )}

          {selectedFile && !uploadToken ? (
            <Button onClick={() => void upload()} disabled={isUploading}>
              {isUploading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              Upload and map columns
            </Button>
          ) : null}

          {isMapping ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Mapping columns...
            </div>
          ) : null}

          {sheetInfo && !isMapping ? (
            <Tabs
              value={selectedSheet ?? Object.keys(sheetInfo)[0]}
              onValueChange={(sheetName) => {
                setSelectedSheet(sheetName);
                setDryRunResult(null);
                setApplyResult(null);
              }}
            >
              <TabsList>
                {Object.keys(sheetInfo).map((sheetName) => (
                  <TabsTrigger key={sheetName} value={sheetName}>
                    {sheetName}
                  </TabsTrigger>
                ))}
              </TabsList>
              {Object.entries(sheetInfo).map(([sheetName, info]) => (
                <TabsContent
                  key={sheetName}
                  value={sheetName}
                  className="space-y-4"
                >
                  {(info.mapping_warnings?.length ?? 0) > 0 ? (
                    <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                      {info.mapping_warnings?.map((warning, index) => (
                        <div key={`${warning.field_key}-${index}`}>
                          {warning.message}
                        </div>
                      ))}
                    </div>
                  ) : null}
                  <div>
                    <div className="mb-2 text-sm font-medium">
                      Column mapping
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {GOVERNMENT_USER_FIELDS.map((field) => (
                        <label
                          className="flex items-center justify-between gap-2 text-xs"
                          key={field.key}
                        >
                          <span>{field.label}</span>
                          <select
                            className="min-w-40 rounded border px-2 py-1"
                            value={fieldMappings[sheetName]?.[field.key] ?? ""}
                            onChange={(event) => {
                              setFieldMappings((current) => ({
                                ...current,
                                [sheetName]: {
                                  ...current[sheetName],
                                  [field.key]: event.target.value,
                                },
                              }));
                              setDryRunResult(null);
                              setApplyResult(null);
                            }}
                          >
                            <option value="">(Not mapped)</option>
                            {info.headers.map((header) => (
                              <option key={header} value={header}>
                                {header}
                              </option>
                            ))}
                          </select>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      onClick={() => void dryRun(sheetName)}
                      disabled={isTesting}
                    >
                      {isTesting ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Test upsert
                    </Button>
                  </div>
                  {dryRunResult && selectedSheet === sheetName ? (
                    <ResultDetails
                      result={dryRunResult}
                      filenamePrefix="government-user-dry-run-errors"
                    />
                  ) : null}
                  {applyResult && selectedSheet === sheetName ? (
                    <div>
                      <div className="mb-2 text-sm font-semibold">
                        Upsert results
                      </div>
                      <ResultDetails
                        result={applyResult}
                        filenamePrefix="government-user-upsert-errors"
                      />
                    </div>
                  ) : null}
                </TabsContent>
              ))}
            </Tabs>
          ) : null}
          {error ? (
            <div className="text-sm text-destructive">{error}</div>
          ) : null}
        </div>
        <SheetFooter className="flex-row justify-between gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {applyResult ? "Close" : "Cancel"}
          </Button>
          <Button
            onClick={() => void apply()}
            disabled={
              isApplying ||
              !dryRunResult ||
              dryRunResult.success_count === 0 ||
              !!applyResult
            }
          >
            {isApplying ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Upsert Government Users
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
